import * as Three from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import Reveal, { revealState } from './reveal';
import SplatLayer, { PART } from './splats';

const ROOM_NODE = /^room-(\d+)-(plan|in|out|sky|back|backout|splat|splatout|splatback|splatfill)$/;
const STEP_ANGLE = Math.PI / 4;     // W/S go to the capture point within ±45° of ahead / behind
const CLICK_ANGLE = Math.PI / 7;    // a click on the view glides to the capture point within ±25° of it
const TURN_STEP = Math.PI / 6;      // A/D, ←/→: turn 30°
const LOOK_SPEED = 0.004;      // rad per dragged pixel
const OCCLUSION_MS = 200;      // how often floor markers are checked against walls (small depth pass)
const OCCLUSION_W = 256;       // width of that depth pass (height follows the view)
const OCCLUSION_SLACK = 0.35;  // m: a surface this much nearer than the marker hides it
const AI_TINT = 0xff6ad5;      // "AI areas" on: painted back layers drawn pink
const COMPACT_W = 560;         // px: narrower than this (the admin panel), the bar shows icons only
const KEYS = { w: 'f', arrowup: 'f', s: 'b', arrowdown: 'b', a: 'l', arrowleft: 'l', d: 'r', arrowright: 'r' };
const normalizeAngle = a => Math.atan2(Math.sin(a), Math.cos(a));

// White line icons in the style of the tour's bottom bar (currentColor).
const svg = body => `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;
const ICONS = {
  overview: svg('<path d="M12 3 3.5 7.5v9L12 21l8.5-4.5v-9z"/><path d="M3.5 7.5 12 12l8.5-4.5M12 12v9"/>'),
  walk: svg('<circle cx="13" cy="4.5" r="1.8" fill="currentColor" stroke="none"/><path d="m9 21 2.5-6.5L14 17v4"/>'
    + '<path d="M11.5 14.5 12.5 9l-3 1.5L8 13.5M12.5 9l2.5 3 3 1"/>'),
  fullscreen: svg('<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>'),
  ai: svg('<path d="M12 3l1.8 4.7L18.5 9.5l-4.7 1.8L12 16l-1.8-4.7L5.5 9.5l4.7-1.8z"/><path d="M18 15l.9 2.1L21 18l-2.1.9L18 21l-.9-2.1L15 18l2.1-.9z"/>'),
};

const smoothstep = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const smootherstep = (t) => t * t * t * (t * (t * 6 - 15) + 10);

/**
 * First-person viewer for a built tour model (model.glb from utils/convert3d: y up, metres, plan x/y → x/z like
 * e3plan-3d; nodes room-<scene>-plan = the room built from its drawn outline, coloured by projecting its panorama —
 * what you walk in when present (clean walls/floor anywhere, furniture painted on; faces look inwards, so the
 * overview is a dollhouse); older/--depth-meshes builds: room-<scene>-in = the room's own surfaces, room-<scene>-out = what it sees beyond,
 * room-<scene>-sky = its panorama on a sphere around the capture point, drawn behind everything to fill mesh gaps,
 * room-<scene>-back / -backout = what the photo never saw (behind objects, edge-on surfaces), painted by AI, inside
 * / beyond the drawn room — fills the holes when moving; never visible from the capture point).
 * Builds with Gaussian splats (point clouds room-<scene>-splat / -splatout / -splatback, splats.js) draw those in
 * Overview and while moving: soft photographic blobs instead of triangles, so edges blur instead of tearing.
 * -splatfill (AI fill, convert.py --complete) is what walking around the capture point would show missing,
 * painted by AI; drawn like -splatback, and tinted with it by "AI areas".
 * Modes, as in the convert-3d prototype viewer:
 *   overview — orbit the whole model (every room's own part)
 *   stand    — at a capture point: the room's panorama sphere only = the original photo, no gaps; drag, wheel
 *   fly      — click a floor marker: the camera travels to that capture point and the rooms cross-fade
 * Moving goes from capture point to capture point, like Matterport: every place you stop is the real photo; the
 * reconstructed 3D is only seen during the ~1 s glide (away from its capture point one photo is stretched over
 * surfaces it saw edge-on). A click or W/↑ and S/↓ go to the point that way, A/D and ←/→ turn.
 *
 *   const v = new ModelPreview(container); await v.load(url, [{scene, name, camera: {x, y, height}}]);
 */
export default class ModelPreview {
  constructor(container) {
    this._container = container;
    container.classList.add('e3-c3v');
    container.innerHTML = `
      <div class="e3-c3v__canvas"></div>
      <div class="e3-c3v__markers"></div>
      <div class="e3-c3v__bar">
        <button type="button" data-act="overview" title="See the whole model">${ICONS.overview}<span>Overview</span></button>
        <button type="button" data-act="walk" title="Walk through: click where to go, W/↑ forward, S/↓ back, A/D turn">${ICONS.walk}<span>Walk</span></button>
        <select class="e3-c3v__rooms" title="Go to a room"></select>
        <button type="button" data-act="ai" class="e3-c3v__ai" title="Show the areas painted by AI (behind furniture, door edges)" hidden>${ICONS.ai}<span>AI areas</span></button>
        <button type="button" data-act="fullscreen" class="e3-c3v__fs" title="Full screen">${ICONS.fullscreen}</button>
      </div>
      <div class="e3-c3v__hint"></div>`;
    this._canvasHost = container.querySelector('.e3-c3v__canvas');
    this._markersEl = container.querySelector('.e3-c3v__markers');
    this._hintEl = container.querySelector('.e3-c3v__hint');
    this._select = container.querySelector('.e3-c3v__rooms');

    this._renderer = new Three.WebGLRenderer({ antialias: true });
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this._renderer.setClearColor(0x1b1b1b);
    this._renderer.outputEncoding = Three.sRGBEncoding;
    this._renderer.autoClear = false;
    this._canvasHost.appendChild(this._renderer.domElement);
    this._camera = new Three.PerspectiveCamera(60, 1, 0.05, 500);
    this._controls = new OrbitControls(this._camera, this._renderer.domElement);
    this._controls.enableDamping = true;
    this._target = new Three.WebGLRenderTarget(1, 1);
    this._fade = new Three.Mesh(new Three.PlaneGeometry(2, 2), new Three.MeshBasicMaterial({
      map: this._target.texture, transparent: true, depthTest: false, depthWrite: false }));
    this._fadeScene = new Three.Scene();
    this._fadeScene.add(this._fade);
    this._fadeCamera = new Three.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    // floor markers hide behind walls: a small depth render a few times a second, read back once
    this._depthTarget = new Three.WebGLRenderTarget(OCCLUSION_W, OCCLUSION_W / 2);
    this._depthMaterial = new Three.MeshDepthMaterial({ depthPacking: Three.RGBADepthPacking, side: Three.DoubleSide });
    this._depthPixels = new Uint8Array(OCCLUSION_W * OCCLUSION_W / 2 * 4);
    this._lastOcclusion = 0;
    this._showAI = false;

    this._rooms = [];            // [{scene, name, eye: Vector3, floor: Vector3, scene3d, parts: {in, out}, marker}]
    this._mode = 'overview';
    this._yaw = 0;
    this._pitch = 0;
    this._yawTarget = null;
    this._listen();
    this._fit();
    let last = performance.now();
    const loop = (now) => {
      this._frame = requestAnimationFrame(loop);
      this._tick(Math.min(0.05, (now - last) / 1000));
      last = now;
    };
    this._frame = requestAnimationFrame(loop);
  }

  load(url, rooms) {
    return new Promise((resolve, reject) => {
      new GLTFLoader().load(url, (gltf) => {
        this._endReveal();
        this._clearRooms();
        const byScene = {};
        const meshes = [];
        const clouds = [];
        gltf.scene.traverse((node) => {
          if (node.isMesh) meshes.push(node);
          else if (node.isPoints) clouds.push(node);
        });
        meshes.forEach((node) => {
          const m = ROOM_NODE.exec(node.name) || ROOM_NODE.exec(node.parent && node.parent.name);
          const sid = m ? m[1] : 'model';
          const part = m ? m[2] : 'in';
          const scene3d = byScene[sid] || (byScene[sid] = { scene3d: new Three.Scene(), parts: {} });
          node.material = new Three.MeshBasicMaterial({ map: node.material && node.material.map,
            side: part === 'plan' ? Three.FrontSide : Three.DoubleSide, depthWrite: part !== 'sky' });
          if (node.material.map) node.material.map.wrapS = Three.RepeatWrapping;   // plan UVs run past 1 at the seam
          if (part === 'sky') node.renderOrder = -1;   // behind the room's surfaces
          scene3d.scene3d.attach(node);
          scene3d.parts[part] = node;
        });
        // the model's extent without the 30 m panorama spheres
        this._box = new Three.Box3();
        Object.values(byScene).forEach(r => ['plan', 'in', 'out'].forEach(k => r.parts[k] && this._box.expandByObject(r.parts[k])));
        rooms.forEach((room, i) => {
          const built = byScene[String(room.scene)];
          if (!built || !room.camera) return;
          const { x, y, height } = room.camera;
          const marker = document.createElement('button');
          marker.type = 'button';
          marker.className = 'e3-c3v__marker';
          marker.textContent = String(i + 1);
          marker.title = room.name || room.scene;
          marker.addEventListener('click', () => this.goTo(room.scene));
          this._markersEl.appendChild(marker);
          this._rooms.push({ ...built, scene: String(room.scene), name: room.name || String(room.scene), marker,
            eye: new Three.Vector3(x / 1000, height / 1000, y / 1000), floor: new Three.Vector3(x / 1000, 0, y / 1000) });
        });
        if (clouds.length) {   // Gaussian splats: one layer for all rooms, the point clouds are only its source data
          const layer = new SplatLayer();
          clouds.forEach((node) => {
            const m = ROOM_NODE.exec(node.name) || ROOM_NODE.exec(node.parent && node.parent.name);
            if (m && m[2] in PART) layer.add(node, this._rooms.findIndex(r => r.scene === m[1]), PART[m[2]]);
            if (m && (m[2] === 'splatback' || m[2] === 'splatfill')) this._aiSplats = true;
          });
          if (layer.count) {
            layer.build();
            this._splats = layer;
            console.info(`[convert3d] Gaussian splats: ${layer.count} (Overview and moving between points)`);
          }
          clouds.forEach((node) => { node.geometry.dispose(); node.material.dispose(); });
        }
        // rooms the report doesn't list (older builds without node names): show them in every mode
        this._extra = Object.keys(byScene).filter(sid => !this._rooms.some(r => r.scene === sid)).map(sid => byScene[sid]);
        this._container.querySelector('.e3-c3v__ai').hidden = !this._rooms.some(r => r.parts.back || r.parts.backout)
          && !this._aiSplats;
        this._tintAI();
        this._select.innerHTML = '<option value="">Go to room…</option>';
        this._rooms.forEach((r, i) => {
          const o = document.createElement('option');
          o.value = r.scene;
          o.textContent = `${i + 1}. ${r.name}`;
          this._select.appendChild(o);
        });
        this.overview();
        this._startReveal();
        resolve();
      }, undefined, reject);
    });
  }

  overview() {
    this._endReveal();
    this._setMode('overview');
    if (!this._box || this._box.isEmpty()) return;
    const center = this._box.getCenter(new Three.Vector3());
    const size = this._box.getSize(new Three.Vector3());
    const r = Math.max(size.x, size.z, 3);
    this._controls.target.copy(center);
    this._camera.position.set(center.x + r * 0.6, center.y + r * 1.1, center.z + r * 0.9);
    this._camera.fov = 60;
    this._camera.updateProjectionMatrix();
    this._controls.update();
  }

  // Stand at a room's capture point: fly there from another room, jump there from the overview.
  goTo(sceneId) {
    const room = this._room(sceneId);
    if (!room) return;
    if (this._reveal && this._reveal.start !== null) this._endReveal();   // not the goTo right after load()
    if (this._mode === 'stand' && this._current !== room) return this._flyTo(room);
    this._standAt(room);
  }

  // Into first person: the capture point nearest to the overview camera.
  walk() {
    this._endReveal();
    if (this._mode !== 'overview') return;
    const start = this._nearest() || this._rooms[0];
    if (start) this._standAt(start);
  }

  // Glide to the capture point best lined up with direction `yaw` (same convention as the view), within `maxAngle`.
  step(yaw, maxAngle = STEP_ANGLE) {
    if (this._mode !== 'stand' || !this._current) return false;
    const here = this._current.eye;
    let best = null;
    let bestScore = Infinity;
    this._rooms.forEach((room) => {
      if (room === this._current) return;
      const dx = room.eye.x - here.x;
      const dz = room.eye.z - here.z;
      const off = Math.abs(normalizeAngle(Math.atan2(dx, -dz) - yaw));
      const score = Math.hypot(dx, dz) * (1 + 2 * off);
      if (off <= maxAngle && score < bestScore) { best = room; bestScore = score; }
    });
    if (best) this._flyTo(best);
    else this._flash('No capture point that way');
    return !!best;
  }

  _flash(text) {
    this._hintEl.textContent = text;
    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => this._setMode(this._mode), 1500);
  }

  toggleFullscreen() {
    const el = this._container;
    const doc = document;
    if (doc.fullscreenElement || doc.webkitFullscreenElement) {
      (doc.exitFullscreen || doc.webkitExitFullscreen).call(doc);
    } else {
      const req = el.requestFullscreen || el.webkitRequestFullscreen;
      if (req) req.call(el);
    }
  }

  destroy() {
    cancelAnimationFrame(this._frame);
    this._endReveal();
    this._unlisten();
    if (document.fullscreenElement === this._container) document.exitFullscreen();
    this._controls.dispose();
    this._clearRooms();
    this._target.dispose();
    this._depthTarget.dispose();
    this._depthMaterial.dispose();
    this._fade.geometry.dispose();
    this._fade.material.dispose();
    this._renderer.dispose();
    this._container.innerHTML = '';
    this._container.classList.remove('e3-c3v');
  }

  // --- internals ---

  _room(sceneId) { return this._rooms.find(r => r.scene === String(sceneId)); }

  // --- intro: points assembling into the rooms, then the real surfaces fade in (reveal.js) ---

  _startReveal() {
    this._endReveal();
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const meshes = [];
    this._rooms.forEach(r => ['in', 'out', 'plan'].forEach(k => r.parts[k] && meshes.push(r.parts[k])));
    if (this._splats) {   // splats do the intro themselves (they grow into place in the wave)
      this._reveal = { splat: true, start: null };
      return;
    }
    if (!meshes.length) return;
    this._reveal = { fx: new Reveal(meshes), start: null };
  }

  _endReveal() {
    if (!this._reveal) return;
    if (this._reveal.fx) this._reveal.fx.dispose();
    if (this._splats) this._splats.setReveal(1);
    this._reveal = null;
    this._setSurfaceOpacity(1);
  }

  _setSurfaceOpacity(a) {
    [...this._rooms, ...(this._extra || [])].forEach(room => Object.values(room.parts).forEach((mesh) => {
      const m = mesh.material;
      const transparent = a < 1;
      if (m.transparent !== transparent) { m.transparent = transparent; m.needsUpdate = true; }
      m.opacity = a;
    }));
  }

  // dot size in pixels: about the angle between neighbouring mesh vertices (~1°), so the cloud looks solid
  _pointSize() {
    const h = this._renderer.getDrawingBufferSize(new Three.Vector2()).y;
    return Math.min(16, Math.max(2, 1.5 * h / this._camera.fov));
  }

  _drawReveal(r, cam) {
    const rv = this._reveal;
    const now = performance.now();
    if (rv.start === null) {   // first frame: the wave starts where you look from
      rv.start = now;
      rv.origin = this._mode === 'overview' && this._box ? this._box.getCenter(new Three.Vector3()) : cam.position.clone();
      rv.maxDist = 1;
      if (this._box) {
        [this._box.min, this._box.max].forEach(a => [this._box.min, this._box.max].forEach(b =>
          [this._box.min, this._box.max].forEach(c =>
            { rv.maxDist = Math.max(rv.maxDist, rv.origin.distanceTo(new Three.Vector3(a.x, b.y, c.z))); })));
      }
    }
    const state = revealState(now - rv.start);
    if (state.done) return this._endReveal();
    this._setSurfaceOpacity(state.surfaces);
    if (rv.splat) {   // drawn by _render with the rest of the splats
      this._splats.setReveal(state.progress, rv.origin, rv.maxDist);
      return undefined;
    }
    rv.fx.update(state, rv.origin, rv.maxDist, this._pointSize());
    r.render(rv.fx.scene, cam);
    return undefined;
  }

  _standAt(room) {
    this._current = room;
    this._camera.position.copy(room.eye);
    if (this._mode === 'overview') {   // face the middle of the model, like walking in from the overview
      const c = this._box.getCenter(new Three.Vector3());
      this._yaw = Math.atan2(c.x - room.eye.x, -(c.z - room.eye.z));
      this._pitch = 0;
    }
    this._camera.fov = 75;
    this._camera.updateProjectionMatrix();
    this._setMode('stand');
  }

  _flyTo(room) {
    const from = this._current || this._nearest();
    const distance = this._camera.position.distanceTo(room.eye);
    this._flight = { from, to: room, start: performance.now(), duration: Math.min(1600, Math.max(700, distance * 300)),
      a: this._camera.position.clone(), b: room.eye.clone() };
    this._setMode('fly');
  }

  _nearest() {
    let best = null;
    let bestD = Infinity;
    this._rooms.forEach((r) => {
      const d = (r.eye.x - this._camera.position.x) ** 2 + (r.eye.z - this._camera.position.z) ** 2;
      if (d < bestD) { bestD = d; best = r; }
    });
    return best;
  }

  _setMode(mode) {
    this._mode = mode;
    this._controls.enabled = mode === 'overview';
    this._container.dataset.mode = mode;
    this._container.querySelectorAll('[data-act]').forEach(b => b.classList.toggle('is-on',
      b.dataset.act === mode || (b.dataset.act === 'walk' && (mode === 'stand' || mode === 'fly')) ||
      (b.dataset.act === 'ai' && this._showAI)));
    this._select.value = (mode === 'stand' && this._current) ? this._current.scene : '';
    this._hintEl.textContent = {
      overview: 'Drag to orbit · scroll to zoom · click a number to stand there',
      stand: 'Drag to look · click where to go · W/↑ forward · S/↓ back · A/D turn',
      fly: '',
    }[mode];
    if (this._showAI && mode !== 'fly') this._hintEl.textContent = 'Pink = painted by AI, never seen by the camera · visible in Overview and while moving between points';
  }

  _tick(dt) {
    const cam = this._camera;
    if (this._yawTarget !== null) {   // A/D turn, eased
      const d = normalizeAngle(this._yawTarget - this._yaw);
      this._yaw += d * Math.min(1, dt * 10);
      if (Math.abs(d) < 0.002) this._yawTarget = null;
    }
    let mix = 0;
    if (this._mode === 'fly') {
      const t = Math.min(1, (performance.now() - this._flight.start) / this._flight.duration);
      cam.position.lerpVectors(this._flight.a, this._flight.b, smootherstep(t));
      mix = smoothstep(0.35, 0.7, t);
      if (t >= 1) {
        const to = this._flight.to;
        this._flight = null;
        this._current = to;
        this._setMode('stand');
      }
    }
    if (this._mode === 'overview') {
      this._controls.update();
    } else {
      const p = cam.position;
      cam.lookAt(p.x + Math.sin(this._yaw) * Math.cos(this._pitch), p.y + Math.sin(this._pitch),
        p.z - Math.cos(this._yaw) * Math.cos(this._pitch));
    }
    this._render(mix);
    this._placeMarkers();
  }

  // which parts of a room to draw: 'own' (in + back), 'near' (in + out + back + backout + sky),
  // 'photo' (sky only, if built with one: the original photo, nothing painted)
  // A room built from the plan: 'own' = the shell, 'near' = shell + panorama sphere, 'photo' = the sphere.
  _show(room, how) {
    const { in: own, out, sky, back, backout, plan } = room.parts;
    if (plan) {
      plan.visible = how !== 'photo';
      if (sky) sky.visible = how !== 'own';
      return;
    }
    if (back) back.visible = how !== 'photo';
    if (backout) backout.visible = how === 'near';
    const photo = how === 'photo' && sky;
    if (own) own.visible = !photo;
    if (out) out.visible = how === 'near' || (how === 'photo' && !sky);
    if (sky) sky.visible = how !== 'own';
  }

  _render(mix) {
    const r = this._renderer;
    const cam = this._camera;
    r.setRenderTarget(null);
    r.clear();
    if (this._reveal && this._mode !== 'fly') this._drawReveal(r, cam);   // points first, surfaces fade in over them
    const extra = () => (this._extra || []).forEach(e => r.render(e.scene3d, cam));
    const sp = this._splats;
    if (sp && (this._mode === 'overview' || this._mode === 'fly' || (this._mode === 'stand' && this._reveal))) {
      // splats: every room's own part; while moving, the nearest room's view beyond its walls and painted areas too
      const near = this._mode === 'overview' ? null : (this._mode === 'fly' ? this._nearest() : this._current);
      sp.setNear(near ? this._rooms.indexOf(near) : -1);
      sp.sort(cam);
      r.render(sp.scene, cam);
      if (this._mode !== 'stand') { extra(); return; }
      // standing during the intro: the real photo fades in over the splats (stand branch below)
    }
    if (this._mode === 'overview') {
      this._rooms.forEach(room => { this._show(room, 'own'); r.render(room.scene3d, cam); });
      extra();
    } else if (this._mode === 'fly' && this._flight && this._flight.to.parts.plan) {
      // plan-built rooms (--plan-shell) are one consistent model: flying just moves through it, no cross-fade
      const near = this._nearest();
      if (near && near.parts.sky) {   // its panorama behind everything: gaps show the right colours, not black
        this._show(near, 'photo');
        r.render(near.scene3d, cam);
      }
      this._rooms.forEach(room => { if (room !== near) { this._show(room, 'own'); r.render(room.scene3d, cam); } });
      extra();
      if (near) { this._show(near, 'near'); r.render(near.scene3d, cam); }
    } else if (this._mode === 'fly' && this._flight) {
      const { from, to } = this._flight;
      if (from) { this._show(from, 'near'); r.render(from.scene3d, cam); }
      this._show(to, 'near');
      r.setRenderTarget(this._target);
      r.clear();
      r.render(to.scene3d, cam);
      r.setRenderTarget(null);
      this._fade.material.opacity = from ? mix : 1;
      r.render(this._fadeScene, this._fadeCamera);
    } else if (this._current) {   // stand
      this._show(this._current, 'photo');
      r.render(this._current.scene3d, cam);
    }
  }

  _tintAI() {
    this._rooms.forEach(room => ['back', 'backout'].forEach(k => room.parts[k] &&
      room.parts[k].material.color.set(this._showAI ? AI_TINT : 0xffffff)));
    if (this._splats) this._splats.setTint(this._showAI ? AI_TINT : null);
  }

  // Depth of the real surfaces (no panorama spheres, no AI layers) at low resolution, for marker occlusion.
  _occlusionPass() {
    const r = this._renderer;
    const cam = this._camera;
    const t = this._depthTarget;
    const h = Math.max(16, Math.round(OCCLUSION_W / cam.aspect));
    if (t.height !== h) { t.setSize(OCCLUSION_W, h); this._depthPixels = new Uint8Array(OCCLUSION_W * h * 4); }
    const clear = r.getClearColor(new Three.Color());
    const alpha = r.getClearAlpha();
    r.setRenderTarget(t);
    r.setClearColor(0xffffff, 1);   // packs to depth 1 = nothing there
    r.clear();
    this._rooms.forEach((room) => {
      const near = room === this._current && this._mode !== 'overview';
      this._show(room, near ? 'near' : 'own');
      if (room.parts.sky) room.parts.sky.visible = false;
      if (room.parts.back) room.parts.back.visible = false;
      if (room.parts.backout) room.parts.backout.visible = false;
      room.scene3d.overrideMaterial = this._depthMaterial;
      r.render(room.scene3d, cam);
      room.scene3d.overrideMaterial = null;
    });
    r.setRenderTarget(null);
    r.setClearColor(clear, alpha);
    r.readRenderTargetPixels(t, 0, 0, t.width, t.height, this._depthPixels);
    this._occlusionReady = true;
  }

  // metres from the camera of the nearest real surface at NDC (x, y), or Infinity
  _sceneDistance(x, y) {
    const t = this._depthTarget;
    const px = Math.min(t.width - 1, Math.max(0, Math.round((x + 1) / 2 * (t.width - 1))));
    const py = Math.min(t.height - 1, Math.max(0, Math.round((y + 1) / 2 * (t.height - 1))));
    const i = (py * t.width + px) * 4;
    const b = this._depthPixels;
    // three.js unpackRGBAToDepth (RGBADepthPacking): x least significant … w most
    const depth = (255 / 256) * (b[i] / 255 / 16777216 + b[i + 1] / 255 / 65536 + b[i + 2] / 255 / 256 + b[i + 3] / 255);
    return depth >= 0.9999 ? Infinity : this._viewDistance(depth);
  }

  _viewDistance(depth) {   // window depth 0..1 → metres along the view axis
    const { near, far } = this._camera;
    return (near * far) / (far - depth * (far - near));
  }

  _placeMarkers() {
    const w = this._canvasHost.clientWidth;
    const h = this._canvasHost.clientHeight;
    const v = new Three.Vector3();
    const now = performance.now();
    if (this._mode !== 'fly' && this._rooms.length && now - this._lastOcclusion > OCCLUSION_MS) {
      this._lastOcclusion = now;
      this._occlusionPass();
    }
    this._rooms.forEach((room) => {
      v.copy(room.floor).project(this._camera);
      let hidden = this._mode === 'fly' || (room === this._current && this._mode !== 'overview') ||
        v.z < -1 || v.z > 1 || Math.abs(v.x) > 0.95 || Math.abs(v.y) > 0.95;
      if (!hidden && this._occlusionReady && this._mode !== 'overview') {
        hidden = this._sceneDistance(v.x, v.y) < this._viewDistance((v.z + 1) / 2) - OCCLUSION_SLACK;
      }
      room.marker.hidden = hidden;
      if (!hidden) {
        room.marker.style.left = `${(v.x + 1) * w / 2}px`;
        room.marker.style.top = `${(1 - v.y) * h / 2}px`;
      }
    });
  }

  _fit() {
    const w = this._canvasHost.clientWidth || 300;
    const h = this._canvasHost.clientHeight || 220;
    this._container.classList.toggle('is-compact', w < COMPACT_W);
    this._renderer.setSize(w, h);
    const size = this._renderer.getDrawingBufferSize(new Three.Vector2());
    this._target.setSize(size.x, size.y);
    this._camera.aspect = w / h;
    this._camera.updateProjectionMatrix();
  }

  _clearRooms() {
    if (this._splats) this._splats.dispose();
    this._splats = null;
    [...this._rooms, ...(this._extra || [])].forEach(room => room.scene3d.traverse((node) => {
      if (!node.isMesh) return;
      node.geometry.dispose();
      if (node.material.map) node.material.map.dispose();
      node.material.dispose();
    }));
    this._rooms.forEach(r => r.marker.remove());
    this._rooms = [];
    this._extra = [];
    this._aiSplats = false;
    this._current = null;
  }

  _listen() {
    const canvas = this._renderer.domElement;
    this._on = [];
    const on = (target, type, fn, opts) => { target.addEventListener(type, fn, opts); this._on.push([target, type, fn, opts]); };
    on(canvas, 'pointerdown', (e) => {
      this._endReveal();   // any input skips the intro
      if (this._mode === 'overview' || this._mode === 'fly') return;
      this._drag = { x: e.clientX, y: e.clientY, yaw: this._yaw, pitch: this._pitch };
      canvas.setPointerCapture(e.pointerId);
    });
    on(canvas, 'pointermove', (e) => {
      if (!this._drag) return;
      this._yaw = this._drag.yaw - (e.clientX - this._drag.x) * LOOK_SPEED;
      this._pitch = Math.max(-1.45, Math.min(1.45, this._drag.pitch + (e.clientY - this._drag.y) * LOOK_SPEED));
    });
    const end = () => { this._drag = null; };
    on(canvas, 'pointerup', (e) => {   // a click (not a drag) goes towards where you clicked
      const drag = this._drag;
      end();
      if (!drag || this._mode !== 'stand' || Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 5) return;
      const rect = canvas.getBoundingClientRect();
      const dir = new Three.Vector3((e.clientX - rect.left) / rect.width * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1, 0.5).unproject(this._camera).sub(this._camera.position);
      this.step(Math.atan2(dir.x, -dir.z), CLICK_ANGLE);   // a click picks within ±25° of where you clicked
    });
    on(canvas, 'pointercancel', end);
    on(canvas, 'wheel', (e) => {
      if (this._mode !== 'stand') return;
      e.preventDefault();
      this._camera.fov = Math.max(35, Math.min(110, this._camera.fov + e.deltaY * 0.03));
      this._camera.updateProjectionMatrix();
    }, { passive: false });
    // capture phase on window, so the panorama editor behind doesn't also get the arrow keys
    on(window, 'keydown', (e) => {
      const key = KEYS[(e.key || '').toLowerCase()];
      if (!key || /INPUT|SELECT|TEXTAREA/.test(e.target.tagName) || e.target.isContentEditable) return;
      if (this._mode !== 'stand' || !this._container.isConnected || e.repeat) return;
      this._endReveal();
      e.preventDefault();
      e.stopPropagation();
      if (key === 'f') this.step(this._yaw);
      if (key === 'b') this.step(this._yaw + Math.PI);
      if (key === 'l' || key === 'r') {
        this._yawTarget = (this._yawTarget === null ? this._yaw : this._yawTarget) + (key === 'l' ? -TURN_STEP : TURN_STEP);
      }
    }, true);
    on(window, 'resize', () => this._fit());
    const fs = () => {
      const full = (document.fullscreenElement || document.webkitFullscreenElement) === this._container;
      this._container.classList.toggle('is-fullscreen', full);
      this._container.querySelector('.e3-c3v__fs').title = full ? 'Exit full screen' : 'Full screen';
      setTimeout(() => this._fit(), 50);
    };
    on(document, 'fullscreenchange', fs);
    on(document, 'webkitfullscreenchange', fs);
    this._container.querySelector('.e3-c3v__bar').addEventListener('click', (e) => {
      const el = e.target.closest && e.target.closest('[data-act]');
      const act = el && el.dataset.act;
      if (act === 'overview') this.overview();
      if (act === 'walk') this.walk();
      if (act === 'fullscreen') this.toggleFullscreen();
      if (act === 'ai') {
        this._showAI = !this._showAI;
        this._tintAI();
        this._setMode(this._mode);
      }
    });
    this._select.addEventListener('change', () => { if (this._select.value) this.goTo(this._select.value); });
  }

  _unlisten() {
    (this._on || []).forEach(([target, type, fn, opts]) => target.removeEventListener(type, fn, opts));
    this._on = [];
  }
}
